import './utils/setupConsoleInterception'; // Must be first to capture all logs
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux'
import App from './App';
import reportWebVitals from './reportWebVitals';
import store from './store/store';
import { AppInitializer } from './initialization/AppInitializer';
import LoadingScreen from './features/Auth/components/LoadingScreen/LoadingScreen';
import './i18n';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { decodeMeetingToken } from "./features/Auth/store/authThunks";

const AppBootstrap = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isMonthlyReportRoute = window.location.pathname.startsWith('/monthlyReport');
  const hasSecurityParams = urlParams.has('s') && urlParams.has('t');
  const [autoJoinReady, setAutoJoinReady] = React.useState(false);
  const [bootstrapState, setBootstrapState] = React.useState({
    status: 'initializing',
    error: null,
    progress: 'Starting initialization...'
  });
   React.useEffect(() => {
    const runAutoJoin = async () => {
      try {
        const url = new URL(window.location.href);
        const secureToken = url.searchParams.get("m");
        const roomId = url.searchParams.get("room_id");
        const bookingId = url.searchParams.get("booking_id");
        if (secureToken) {
          const r = await store.dispatch(
            decodeMeetingToken({ params: [{ token: secureToken }] })
          ).unwrap();

          if (r?.ok && r?.data) {
            sessionStorage.setItem("autojoin_room_id", r.data.room_id);
            sessionStorage.setItem("autojoin_booking_id", r.data.booking_id);
          }

          const cleanUrl = `${url.origin}${url.pathname}${url.hash || ""}`;
          window.history.replaceState({}, document.title, cleanUrl);
        } else if (roomId && bookingId) {
        sessionStorage.setItem("autojoin_room_id", String(roomId));
        sessionStorage.setItem("autojoin_booking_id", String(bookingId));
      }
      } catch (e) {
        console.error("[AutoJoin] error", e);
      }
      setAutoJoinReady(true);
    };

    runAutoJoin();
  }, []);

  React.useEffect(() => {
        if (!autoJoinReady) return;
    const bootstrap = async () => {
      try {
        // Phase 1: Get preloaded state (includes Keycloak init)
        setBootstrapState({
          status: 'initializing',
          error: null,
          progress: 'Initializing authentication...'
        });

        let preloadedState = { auth: {} };
        if (isMonthlyReportRoute && hasSecurityParams) {
          const { getKeycloakSettings } = await import('./features/Auth/utils/AuthUtils');
          const settings = await getKeycloakSettings(); 
          preloadedState.auth = {
            tenant: settings.tenant,
            isAuthenticated: false,
            keycloakSettings: settings.keycloakSettings
          };
        } else {
          // On exécute l'initialisation normale (qui contient Keycloak)
          preloadedState = await AppInitializer.getPreloadedState();
        }


        // Phase 2: Update store with preloaded state
        setBootstrapState({
          status: 'initializing',
          error: null,
          progress: 'Setting up application state...'
        });

        // Manually populate the store with preloaded state
        if (preloadedState.auth) {
          const {
            keycloakSettings,
            tenant,
            livekitUrl,
            isAuthenticated,
            token,
          } = preloadedState.auth;

          // Import actions here to avoid circular dependencies
          const { setKeycloakSettings, setTenant, setLivekitUrl, loginSuccess } =
            await import('./features/Auth/store/authSlice');
          if (keycloakSettings) store.dispatch(setKeycloakSettings(keycloakSettings));
          if (tenant) store.dispatch(setTenant(tenant));
          if (livekitUrl) store.dispatch(setLivekitUrl(livekitUrl));
          if (isAuthenticated && token) {
            store.dispatch(loginSuccess(token));
          }
        }

        // Phase 3: Initialize post-store services
        setBootstrapState({
          status: 'initializing',
          error: null,
          progress: 'Starting application services...'
        });

// On ne les lance QUE si on n'est pas dans le mode "Rapport Public"
      if (!(isMonthlyReportRoute && hasSecurityParams)) {
        AppInitializer.initializePostStoreServices(store, preloadedState);
      }

        setBootstrapState({ status: 'ready', error: null, progress: 'Ready!' });

      } catch (error) {
        console.error('Application bootstrap failed:', error);
        setBootstrapState({
          status: 'error',
          error: error.message || 'Unknown initialization error',
          progress: 'Initialization failed'
        });
      }
    };

    bootstrap();
  }, [autoJoinReady , isMonthlyReportRoute]);

  if (!autoJoinReady) {
    return <LoadingScreen>Preparing meeting...</LoadingScreen>;
  }
  // Error state
  if (bootstrapState.status === 'error') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>
            Application Initialization Failed
          </h2>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            {bootstrapState.error}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Reload Application
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (bootstrapState.status === 'initializing') {
    return (
      <LoadingScreen>
        {bootstrapState.progress}
      </LoadingScreen>
    );
  }

  // Ready state
  return (
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<AppBootstrap />);

reportWebVitals();
